(ns minorodata.components.pipeline-editor.panel
  (:require
   ["allotment" :refer [Allotment]]
   ["lucide-react" :refer [ChevronsLeftRight ChevronsRightLeft FolderSyncIcon
                           Plus PlusCircleIcon X]]
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :refer [format-date]]
   [minorodata.components.pipeline-editor.analytics-model :refer [analytics-model-page]]
   [minorodata.components.pipeline-editor.dag :refer [new-transform-form
                                                      summary-toggle]]
   [minorodata.components.pipeline-editor.extract :refer [extract-page]]
   [minorodata.components.pipeline-editor.lists :refer [extracts-content
                                                        tables-content
                                                        transforms-content]]
   [minorodata.components.pipeline-editor.sandbox-summary :refer [sandbox-summary]]
   [minorodata.components.pipeline-editor.search :refer [search]]
   [minorodata.components.pipeline-editor.entities-panel :refer [entities-panel]]
   [minorodata.components.pipeline-editor.table :refer [table-page]]
   [minorodata.components.pipeline-editor.transform :refer [transform-page]]
   [minorodata.components.pipeline-editor.utils :refer [close-tab navigate-tab
                                                        review-dialog select-tab
                                                        tab-icon]]
   [minorodata.components.transformations.unit-test :as unit-test]
   [minorodata.sandbox.events :refer [empty-tab]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [ui.alert :as alert]
   [ui.button :as button]
   [ui.chip :refer [chip]]
   [ui.context-menu :refer [context-menu-content context-menu-item
                            context-menu-root context-menu-trigger]]
   [ui.drawer]
   [ui.popover :as popover]
   [ui.tabs :as tabs :refer [trigger-class]]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn separator-with-text [{:keys [text]}]
  [:div {:class "w-full relative"}
   [:div {:class "border-b"}]
   [:div {:class "absolute left-1/2 px-2 bg-kleene-background -translate-x-1/2 -translate-y-1/2"}
    text]])

(defn picker-column [{:keys [type title chip-el list-class content]}]
  (let [collapsed? @(re-posh/subscribe [:pipeline-editor.subs/picker-column-collapsed? type])
        toggle     #(dispatch [:pipeline-editor.events/toggle-picker-column type])]
    (if collapsed?
      [:div {:class    "shrink-0 self-stretch w-12 flex flex-col border rounded-lg overflow-hidden cursor-pointer hover:bg-kleene-hover-background transition-colors"
             :title    (str "Expand " title)
             :on-click toggle}
       [:div {:class "flex flex-col items-center justify-end gap-2 py-3"}
        [:> ChevronsLeftRight {:size 24 :class "text-kleene-text-secondary opacity-50"}]
        [tab-icon {:type type :size 24}]]
       [:div {:class "flex justify-center"}
        [:span {:class "[writing-mode:vertical-rl] rotate-180 font-semibold whitespace-nowrap"}
         title]]]
      [:div {:class "flex-1 min-w-0 flex flex-col border rounded-lg overflow-hidden"}
       [:div {:class "flex gap-2 items-center justify-center relative px-2 pt-2 pb-4"}
        [tab-icon {:type type :size 24}]
        [typography/h3 title]
        chip-el
        [button/button {:size      :icon
                        :icon-props {:size 24 :class "text-kleene-text-secondary opacity-50"}
                        :icon      ChevronsRightLeft
                        :variant   :empty-transparent
                        :class     "absolute right-1 "
                        :title     (str "Collapse " title)
                        :on-click  toggle}]]
       [:div {:class (clsx "flex-1 min-h-0" list-class)}
        content]])))

(defn empty-tab-content []
  (let [sandbox?     @(re-posh/subscribe [:sandbox.subs/enabled?])
        live-switch? @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature :live-switch])]
    [:<>
     [:div {:class "absolute top-2 right-2 z-20"}
      [summary-toggle]]

     [:div {:class "grid grid-cols-1 grid-rows-1 overflow-hidden h-full pt-12 pb-8"}
      [:div {:class "flex flex-col gap-8 justify-center items-center overflow-hidden"}
       [typography/h2 "Select an extract, transform or table"]

       [:div {:class "w-2xl"}
        [search]]

       [separator-with-text {:text "pick from the list"}]

       [:div {:class "flex-1 flex gap-2 max-w-6xl w-full overflow-hidden"}
        [picker-column {:type       "extract"
                        :title      "Extracts"
                        :list-class "overflow-auto"
                        :chip-el    (when sandbox?
                                      [chip {:variant :warning
                                             :title   "Extracts are not part of the sandbox and always reflect the live pipeline."}
                                       "Live"])
                        :content    [extracts-content]}]

        [picker-column {:type       "transform"
                        :title      "Transforms"
                        :list-class "overflow-auto"
                        :content    [transforms-content]}]

        [picker-column {:type       "table"
                        :title      "Tables"
                        :list-class "overflow-y-auto"
                        :content    [tables-content]}]]

       (when live-switch?
         [separator-with-text {:text "or"}])

       (if sandbox?
         [:div {:class "flex *:flex-1 gap-2 w-96"}
          [popover/popover-root
           [popover/popover-trigger {:as-child true}
            [:> button/button-fr {:variant :secondary}
             [:div {:class "flex gap-4 pl-1"}
              [:div [:> PlusCircleIcon {:class "text-kleene-text-success"}]]
              [:div
               [typography/h3 "Create new transform"]
               [typography/small {:class "text-kleene-text-secondary"}
                "Modify pipeline in isolated environment"]]]]]
           [popover/popover-content
            [typography/h4 {:class "mb-4"} "Create new transform"]
            [new-transform-form
             {:on-create
              (fn [{:keys [group-id name mode]}]
                (if (= :sql-generator mode)
                  (dispatch [:app.events/navigate-query
                             {:sql-generator                true
                              :sql-generator-group-id       group-id
                              :sql-generator-transform-name name}])
                  (dispatch [:pipeline-editor.events/create-and-open-transform
                             {:group-id  group-id
                              :transform {:name name}}])))}]]]]
         (when live-switch?
           [:div {:class "w-96"}
            [button/button {:variant  :secondary
                            :on-click #(dispatch [:pipeline-editor.events/switch-mode false])}
             [:div {:class "flex gap-4 pl-1"}
              [:div
               [typography/h3 "Open sandbox"]
               [typography/small {:class "text-kleene-text-secondary"}
                "You are viewing the live pipeline. Switch to sandbox to create transforms."]]]]]))]]]))

(defn node-tab [{:keys [name type id loading? hide-close preview]}]
  [tabs/tabs-trigger {:value    id
                      :key      id
                      :variant  :overlined
                      :class    (clsx "gap-2 px-3 font-normal text-xs group shrink-0"
                                      {"italic" preview})
                      :disabled loading?}
   [tab-icon {:type type}]
   name
   [button/button {:size          :icon-xs
                   :icon          X
                   :variant       :empty-transparent
                   :component     :div
                   :class         (clsx "rounded-full transition-all opacity-0 group-hover:opacity-100 -ml-1 -mr-2"
                                        {"hidden" hide-close})
                   :on-mouse-down (fn [e] (.stopPropagation e))
                   :on-click      #(close-tab id)}]])

(defn live-switch []
  (let [live? @(re-posh/subscribe [:pipeline-editor.subs/live-preview?])
        modes [{:label "Sandbox" :active? (not live?) :live false}
               {:label "Live"    :active? live?       :live true}]]
    [:div {:class "flex items-center"}
     [:div {:class "font-medium flex items-center gap-1 bg-kleene-background border p-0.5 rounded"}
      (for [{:keys [label active? live]} modes]
        [:div {:key      label
               :on-click #(dispatch [:pipeline-editor.events/switch-mode live])
               :class    (clsx "cursor-pointer py-0.5 px-2 rounded transition-colors"
                               {"bg-kleene-secondary-v2 text-white" active?
                                "hover:bg-kleene-hover-background" (not active?)})}
         label])]]))

(defn use-hide-after [ms]
  (let [[visible set-visible] (react/useState true)
        hide                  (react/useCallback #(set-visible false) #js [])]
    (react/useEffect
     (fn []
       (if visible
         (let [timer (js/setTimeout #(set-visible false) ms)]
           (fn [] (js/clearTimeout timer)))
         js/undefined))
     #js [visible])
    [visible hide]))

(defn live-alert []
  (let [[visible hide] (use-hide-after 700)]
    [:div {:class    (clsx "overflow-hidden transition-all h-12 border-b border-b-kleene-text-warning/30"
                           {"h-0 border-b-0" (not visible)})
           :title    "Click to dismiss"
           :on-click hide}
     [alert/alert {:variant :warning
                   :class   (clsx "rounded-none transition-colors cursor-pointer"
                                  {"bg-kleene-text-warning/30" (not visible)})}
      [alert/alert-title
       [:strong "Live Mode"]
       "- You are viewing the live pipeline. Switch to Sandbox mode to make changes."]]]))

(defn editor-tabs [{:keys [opened-tabs active-tab-id loading?]}]
  (let [live? @(re-posh/subscribe [:pipeline-editor.subs/live-preview?])]
    [tabs/tabs-root {:value           active-tab-id
                     :variant         :overlined
                     :class           "min-w-0 grid grid-cols-1 grid-rows-[auto_1fr] h-full overflow-hidden flex-1 relative"
                     :activation-mode "manual"
                     :on-value-change #(select-tab %)}

     [context-menu-root
      [context-menu-trigger
       [tabs/tabs-list {:variant :overlined
                        :class   "overflow-x-auto scrollbar-thin"}
        (for [tab opened-tabs]
          ^{:key (:id tab)}
          [node-tab (assoc tab
                           :loading?   loading?
                           :hide-close (and (= (:type tab) "empty")
                                            (= (count opened-tabs) 1)))])
        (when-not (some #(= (:id %) (:id empty-tab)) opened-tabs)
          [:button {:on-click #(navigate-tab empty-tab)
                    :class    (clsx (trigger-class {:variant :overlined})
                                    "py-1.5 font-medium px-3 group sticky right-[139px]")}
           [:> Plus {:size 20}]])
        (when @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature :live-switch])
          [:div {:class "flex items-center ml-auto px-1 sticky right-0 bg-kleene-background-secondary border-b"}
           [live-switch]])]]
      [context-menu-content
       [context-menu-item
        {:on-click #(dispatch [:pipeline-editor.events/close-all-tabs])}
        "Close all tabs"]]]

     (for [{:keys [name type id group-id]} opened-tabs]
       [tabs/tabs-content {:key   id
                           :value id
                           :class "relative overflow-hidden h-full"}
        (when live?
          [:div {:class "absolute top-0 left-0 right-0 z-50"}
           [:f> live-alert]])

        (case type
          "transform"
          [transform-page {:master-id id
                           :group-id  group-id
                           :name      name}]

          "table"
          [table-page {:table-id id
                       :group-id group-id
                       :table    name}]

          "extract"
          [extract-page {:master-id id
                         :group-id  group-id
                         :name      name}]

          "model"
          [analytics-model-page {:master-id id
                                 :group-id  group-id
                                 :name      name}]

          "empty"
          [empty-tab-content])])]))

(defn right-sidebar []
  (let [rebasing?                @(re-posh/subscribe [:mold-http/loading? :sandbox.api/rebase-version])
        sandbox-metadata         @(re-posh/subscribe [:pipeline-editor.subs/sandbox-metadata])
        right-sidebar-view       @(re-posh/subscribe [:pipeline-editor.subs/right-sidebar-view])
        right-sidebar-unit-tests @(re-posh/subscribe [:pipeline-editor.subs/right-sidebar-unit-tests])]
    [:div {:class "flex h-full flex-col border-l bg-kleene-background shadow-2xl overflow-hidden"}
     [:div {:class "border-b p-4 shrink-0"}
      [:div {:class "flex items-start justify-between gap-3"}
       [:div
        [typography/h3 {:class "whitespace-nowrap"}
         (case right-sidebar-view
           :unit-tests "Add unit test"
           "Sandbox summary")]

        (when (not= right-sidebar-view :unit-tests)
          (when-let [created-at (:created-at sandbox-metadata)]
            [:p {:class "mt-1 text-xs text-kleene-text-secondary whitespace-nowrap"}
             (str "Last sync: " (format-date created-at))]))]

       (if (= right-sidebar-view :unit-tests)
         [button/button
          {:variant  :secondary
           :size     :small
           :class    "shrink-0 whitespace-nowrap px-3"
           :on-click #(dispatch [:pipeline-editor.events/reset-right-sidebar-view])}
          "Back to summary"]

         [button/button
          {:variant  :secondary
           :size     :small
           :class    "shrink-0 whitespace-nowrap px-3"
           :icon     FolderSyncIcon
           :disabled rebasing?
           :title    "Sync with live"
           :on-click #(dispatch [:sandbox.events/rebase-sandbox])}
          "Sync"])]]

     ;; Each view owns its scrolling; the shell only bounds the height.
     [:div {:class "min-h-0 flex-1 overflow-hidden"}
      (case right-sidebar-view
        :unit-tests
        [:div {:class "h-full overflow-y-auto"}
         [:f> unit-test/sidebar right-sidebar-unit-tests]]

        [sandbox-summary])]]))

(defn panel' [{:keys [opened-tabs active-tab-id loading? sandbox? horizontal-sizes
                      left-sidebar-collapsed? right-sidebar-collapsed?]}]
  (let [left-sidebar-size        (nth horizontal-sizes 0 280)
        main-panel-size          (nth horizontal-sizes 1 99999)
        right-sidebar-min-size   480
        right-sidebar-size       (nth horizontal-sizes 2 right-sidebar-min-size)
        allotment-sizes          horizontal-sizes
        outer-ref                (react/useRef nil)]

    (react/useEffect
     (fn []
       (when-let [el (jsi/get outer-ref :current)]
         (jsi/call el :resize
                   (clj->js [(if left-sidebar-collapsed?
                               0
                               (max 280 left-sidebar-size))
                             main-panel-size
                             (if right-sidebar-collapsed?
                               0
                               (max right-sidebar-min-size right-sidebar-size))])))
       js/undefined)
     #js [left-sidebar-collapsed? right-sidebar-collapsed?])

    [:<>
     [:> Allotment {:ref           outer-ref
                    :default-sizes (clj->js allotment-sizes)
                    :on-change (debounce
                                #(dispatch [:pipeline-editor.events/set-horizontal-sizes
                                            (vec (take 3 (js->clj %)))])
                                200)}
      [:> Allotment.Pane {:min-size       0
                          :preferred-size (if left-sidebar-collapsed?
                                            0
                                            (max 280 left-sidebar-size))
                          :visible        (not= active-tab-id (:id empty-tab))
                          :snap           true}
       [entities-panel]]

      [:> Allotment.Pane
       [editor-tabs {:opened-tabs   opened-tabs
                     :active-tab-id active-tab-id
                     :loading?      loading?}]]

      [:> Allotment.Pane {:min-size       0
                          :preferred-size (if right-sidebar-collapsed?
                                            0
                                            (max right-sidebar-min-size right-sidebar-size))
                          :visible        sandbox?
                          :snap           true}
       [right-sidebar]]]

     (when sandbox?
       [review-dialog])]))

(defn panel []
  (let [tabs                     @(re-frame/subscribe [:pipeline-editor.subs/tabs])
        active-tab-id            @(re-frame/subscribe [:pipeline-editor.subs/active-tab-id])
        loading?                 @(re-frame/subscribe [:pipeline-editor.subs/tab-loading?])
        sandbox?                 @(re-posh/subscribe [:sandbox.subs/enabled?])
        horizontal-sizes         @(re-posh/subscribe [:pipeline-editor.subs/horizontal-sizes])
        left-sidebar-collapsed?  @(re-posh/subscribe [:pipeline-editor.subs/left-sidebar-collapsed?])
        right-sidebar-collapsed? @(re-posh/subscribe [:pipeline-editor.subs/right-sidebar-collapsed?])]
    [:f> panel' {:opened-tabs              tabs
                 :active-tab-id            active-tab-id
                 :loading?                 loading?
                 :sandbox?                 sandbox?
                 :horizontal-sizes         horizontal-sizes
                 :left-sidebar-collapsed?  left-sidebar-collapsed?
                 :right-sidebar-collapsed? right-sidebar-collapsed?}]))
